import { useEffect, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { HandCoins, Timer } from "lucide-react";
import { Ban, BarChart3, CreditCard, FileBarChart, Handshake, MailCheck, Package, PackageX, Receipt, Repeat, TicketPercent, CalendarDays, Copy, FileInput, Filter, Home, LogOut, Menu, Moon, Search, Send, Settings, ShieldCheck, Sun, Users, Workflow } from "lucide-react";
import { api } from "@/lib/api";
import { resetOpsCache } from "@/lib/stubs";
import { applyTheme, useUserTheme, type Theme } from "@/lib/theme";
import type { Me } from "@/lib/types";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { CommandPalette } from "./CommandPalette";

export const NAV: { to: string; label: string; icon: typeof Home; exact?: boolean; group?: string }[] = [
  { to: "/", label: "Home", icon: Home, exact: true },
  { to: "/contacts", label: "Contacts", icon: Users, exact: true },
  { to: "/contacts/duplicates", label: "Possible duplicates", icon: Copy },
  { to: "/events", label: "Events", icon: CalendarDays },
  { to: "/automations", label: "Automations", icon: Workflow },
  { to: "/forms", label: "Website forms", icon: FileInput },
  { to: "/reports", label: "Saved reports", icon: FileBarChart },
  { to: "/email/broadcasts", label: "Broadcasts", icon: Send, group: "Email" },
  { to: "/email/reports", label: "Reports", icon: BarChart3, group: "Email" },
  { to: "/email/segments", label: "Segments", icon: Filter, group: "Email" },
  { to: "/email/suppressions", label: "Blocked addresses", icon: Ban, group: "Email" },
  { to: "/email/gmail", label: "Gmail log", icon: MailCheck, group: "Email" },
  { to: "/billing/approvals", label: "Approvals", icon: ShieldCheck, group: "Billing" },
  { to: "/billing/orders", label: "Orders and payments", icon: Receipt, group: "Billing" },
  { to: "/billing/delivery-problems", label: "Paid, something missing", icon: PackageX, group: "Billing" },
  { to: "/billing/subscriptions", label: "Subscriptions", icon: Repeat, group: "Billing" },
  { to: "/billing/runs", label: "Billing runs", icon: Timer, group: "Billing" },
  { to: "/billing/products", label: "Products", icon: Package, group: "Billing" },
  { to: "/billing/promotions", label: "Promotions", icon: TicketPercent, group: "Billing" },
  { to: "/affiliates", label: "Affiliates", icon: Handshake, group: "Billing" },
  { to: "/billing/partners", label: "Partners: who to pay", icon: HandCoins, group: "Billing" },
  { to: "/billing/card-links", label: "Card links (go-live)", icon: CreditCard, group: "Billing" },
  { to: "/billing/club-cards", label: "Club cards to enter (go-live)", icon: CreditCard, group: "Billing" },
  { to: "/settings", label: "Settings", icon: Settings },
];

export function AppShell({ me, children }: { me: Me; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const theme = useUserTheme(me);
  const pending = useQuery({
    queryKey: ["match-reviews-count"],
    queryFn: () => api<{ pending: number }>("/api/v1/match-reviews/count").then((r) => r.pending),
    staleTime: 60_000,
  });
  const toggleTheme = async () => {
    const next: Theme = theme === "dark" ? "light" : "dark";
    applyTheme(next);
    qc.setQueryData(["me"], { ...me, preferences: { ...me.preferences, theme: next } });
    await api("/api/v1/auth/me/preferences", { method: "PUT", json: { theme: next } }).catch(() => undefined);
  };
  const signOut = async () => {
    await api("/api/v1/auth/logout", { method: "POST" });
    resetOpsCache();
    qc.setQueryData(["me"], null);
  };
  const nav =
    "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)] [&.active]:bg-[var(--color-muted)] [&.active]:text-[var(--color-foreground)]";
  let lastGroup: string | undefined;
  return (
    <div className="flex min-h-screen">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <aside className="sticky top-0 hidden h-screen w-52 shrink-0 flex-col gap-0.5 border-r p-3 md:flex">
        <Link to="/" className="mb-4 px-2 text-sm font-semibold">
          CREU CRM
        </Link>
        {NAV.map((n) => {
          const heading = n.group && n.group !== lastGroup ? n.group : null;
          lastGroup = n.group;
          return (
            <div key={n.to}>
              {heading && <div className="mb-0.5 mt-3 px-2 text-xs font-medium text-[var(--color-muted-foreground)]">{heading}</div>}
              <Link to={n.to} className={nav} activeOptions={{ exact: !!n.exact }}>
                <n.icon className="size-4" /> {n.label}
                {n.to === "/contacts/duplicates" && pending.data ? (
                  <span className="num ml-auto rounded-full bg-[var(--color-warning-bg)] px-1.5 text-xs text-[var(--color-warning-fg)]" title={`${pending.data} pairs waiting`}>
                    {pending.data}
                  </span>
                ) : null}
              </Link>
            </div>
          );
        })}
        <div className="mt-auto border-t pt-3 text-xs text-[var(--color-muted-foreground)]">
          <div className="truncate px-2" title={me.email}>
            {me.name}
          </div>
          <button onClick={toggleTheme} className="mt-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 hover:bg-[var(--color-muted)]">
            {theme === "dark" ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </button>
          <button onClick={signOut} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 hover:bg-[var(--color-muted)]">
            <LogOut className="size-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-12 items-center gap-2 border-b bg-[var(--color-background)] px-4">
          <DropdownMenu>
            <DropdownMenuTrigger className="rounded-md p-1.5 hover:bg-[var(--color-muted)] md:hidden" aria-label="Menu">
              <Menu className="size-5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {NAV.map((n) => (
                <DropdownMenuItem key={n.to} asChild>
                  <Link to={n.to}>
                    <n.icon className="size-4" /> {n.group ? `${n.group}: ` : ""}
                    {n.label}
                  </Link>
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem onSelect={toggleTheme}>{theme === "dark" ? "Light mode" : "Dark mode"}</DropdownMenuItem>
              <DropdownMenuItem onSelect={signOut}>Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" className="w-full max-w-md justify-start text-[var(--color-muted-foreground)]" onClick={() => setOpen(true)}>
            <Search /> Find a person…
            <kbd className="ml-auto rounded border px-1.5 text-[10px]">⌘K</kbd>
          </Button>
        </header>
        <main id="main" tabIndex={-1} className="min-w-0 flex-1 p-3 outline-none sm:p-4">
          {children}
        </main>
      </div>
      <CommandPalette open={open} onOpenChange={setOpen} />
    </div>
  );
}
